import {Component, effect, HostListener, Inject, inject, OnDestroy, OnInit} from '@angular/core';
import {Router, RouterLink, RouterLinkActive, NavigationEnd, ActivatedRoute} from "@angular/router";
import {filter} from 'rxjs';
import {SessionStoreDataService} from '../../../projects/products-lib/src/lib/services/session-store-data.service';
import {AuthValidationService, SessionExpireService} from 'auth';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import { ExternalRedirect} from '../../../projects/auth/src/lib/services/external-redirect.service';
import {PupilMemberService} from '../../services/pupil-member.service';

@Component({
  selector: 'app-account-header-component',
    imports: [
        RouterLink,
        RouterLinkActive
    ],
  templateUrl: './account-header-component.html',
  styleUrl: './account-header-component.sass',
})
export class AccountHeaderComponent implements OnInit, OnDestroy {
  isLoggedIn;
  isMenuOpen = false;
  routeContext = '';

  constructor(
    @Inject(ENV_TOKEN)
    protected readonly environment: EnvInterface,
    private sessionStoreDataService: SessionStoreDataService,
    private authValidationService: AuthValidationService,
    private router: Router,
    private route: ActivatedRoute,
    private sessionExpireService: SessionExpireService,
    protected externalRedirect: ExternalRedirect
  ) {
    this.isLoggedIn = this.sessionStoreDataService.isLoggedInSignal;
    // Listen for session expiration signal using effect
    effect(() => {
      if (this.sessionExpireService.sessionExpiredSignal()) {
        this.handleSessionExpiredLogout();
      }
    });
  }

  get isPupilMember(): boolean {
    const decodedToken = this.sessionStoreDataService.getDecodedTokenUser();
    if (!decodedToken) {
      return false;
    }
    return decodedToken.isPupilMember === true;
  }

  ngOnInit(): void {
    this.updateRouteContext();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateRouteContext();
    });
  }

  private updateRouteContext(): void {
    let route = this.route;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.routeContext = route.snapshot.data['routeContext'] || '';
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  // Close menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const burgerButton = document.querySelector('.burger-menu-button');
    const menu = document.querySelector('.burger-menu');

    if (burgerButton && menu && !burgerButton.contains(target) && !menu.contains(target)) {
      this.isMenuOpen = false;
    }
  }

  // Clicks inside an iframe don't bubble to the parent document, so document:click
  // never fires for them. When an iframe is clicked, the parent window loses focus
  // and the iframe becomes the active element. This works for cross-origin iframes too.
  @HostListener('window:blur')
  onWindowBlur(): void {
    if (document.activeElement instanceof HTMLIFrameElement) {
      this.isMenuOpen = false;
    }
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  navigateToCGPBooks(): void {
    // TODO: Verify this is the correct URL format for CGP Books redirect
    this.externalRedirect.ssoRedirect(`${this.environment.app.mediaBaseUrl}?returnurl=${this.environment.app.mediaBaseUrl}`);
    this.isMenuOpen = false;
  }

  navigateToRevisionHub(): void {
    this.router.navigate(['/revision-hub']);
    this.isMenuOpen = false;
  }

  logout() {
    this.authValidationService.revokeAndClearToken().subscribe(() => {
      this.clearStorage();
      this.router.navigate(['/']);
      this.isMenuOpen = false;
    });
  }

  handleSessionExpiredLogout() {
    this.authValidationService.revokeAndClearToken().subscribe(() => {
      this.clearStorage();
      sessionStorage.setItem('logoutReason', 'You were logged out due to inactivity.');
      this.router.navigate(['/login']);
    });
  }

  private readonly pupilIconService = inject(PupilMemberService);

  private clearStorage(){
    this.sessionStoreDataService.removeStoredData('authToken');
    this.sessionStoreDataService.removeStoredData('digitalLicences');
    this.sessionStoreDataService.removeStoredData('auth-token');
    sessionStorage.removeItem('productSelected');
    this.pupilIconService.clearPupilData();
  }

  protected navigateToElevenPlusDashboard() {
    this.isMenuOpen = false;
    if (this.router.url.startsWith('/11-plus-online-tests')) {
      window.location.reload();
    } else {
      this.router.navigate(['/11-plus-online-tests']);
    }
  }

  protected navigateToElevenPlusChangeUser() {
    //11-plus-online
    this.router.navigate([`/11-plus-online-account`]);
    this.isMenuOpen = false;
  }
}
